import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { LoginRequest } from '../../Types/General';
import { Resume, UpdateResumeRequest } from '../../Types/Resume';
import {
  ComposeMessageClient,
  Talent,
  TalentDashboardResponse,
  TalentRegistrationRequest,
  TalentSearchRequest,
  bookmarkJobRequest,
} from '../../Types/Talent';
import { AppSettings } from '../../Utils/Settings';
export const talentApi = createApi({
  reducerPath: 'talentApi',
  baseQuery: fetchBaseQuery({
    baseUrl: AppSettings.ApiBaseUrl,
    prepareHeaders: async (headers, { getState }) => {
      if (!headers.has('Authorization')) {
        headers.set(
          'Authorization',
          `Bearer ${localStorage.getItem('token') ?? ''}`,
        );
      }
      return headers;
    },
  }),
  tagTypes: ['TalentData', 'TalentProfilePicture', 'TalentDashboard'],
  endpoints: builder => ({
    getTalents: builder.query<Talent[], void>({
      query: () => ({
        url: '/talent/all',
        method: 'GET',
      }),
    }),
    getManyTalents: builder.query<Talent[], string[]>({
      query: body => ({
        url: '/talent',
        method: 'POST',
        body,
      }),
    }),
    getTalent: builder.query<Talent, string>({
      providesTags: ['TalentData'],
      query: locator => ({
        url: `/talent/${locator}`,
        method: 'GET',
      }),
    }),
    registerTalents: builder.mutation<
      any,
      { body: TalentRegistrationRequest; affiliate: string | null }
    >({
      query: ({ body, affiliate }) => ({
        url: `/talent/register${affiliate ? '/' + affiliate : ''}`,
        method: 'POST',
        body: body,
      }),
    }),
    loginTalent: builder.mutation<any, LoginRequest>({
      query: body => ({
        url: '/talent/login',
        method: 'POST',
        body,
      }),
    }),
    updateTalent: builder.mutation<any, any>({
      invalidatesTags: ['TalentData', 'TalentDashboard'],
      query: body => ({
        url: `/talent`,
        method: 'PUT',
        body,
      }),
    }),
    deleteTalent: builder.mutation<any, string>({
      query: locator => ({
        url: `/talent/${locator}`,
        method: 'DELETE',
      }),
    }),
    getTalentResume: builder.query<Resume, string>({
      query: locator => ({
        url: `/talent/${locator}/resume`,
        method: 'GET',
      }),
    }),
    createTalentResume: builder.mutation<
      Resume,
      { locator: string; body: Resume }
    >({
      query: ({ locator, body }) => ({
        url: `/talent/${locator}/resume`,
        method: 'POST',
        body,
      }),
    }),
    uploadTalentProfilePicture: builder.mutation<
      any,
      { locator: string; body: any }
    >({
      invalidatesTags: ['TalentProfilePicture'],
      query: ({ locator, body }) => ({
        url: `/talent/${locator}/profile`,
        method: 'POST',
        body,
      }),
    }),
    getTalentProfilePicture: builder.query<string, string>({
      providesTags: ['TalentProfilePicture'],
      query: locator => ({
        url: `/talent/profile-picture/${locator}`,
        method: 'GET',
      }),
    }),
    bookmarkJob: builder.mutation<any, bookmarkJobRequest>({
      invalidatesTags: ['TalentData', 'TalentDashboard'],
      query: body => ({
        url: `/talent/bookmark`,
        method: 'PUT',
        body,
      }),
    }),
    unbookmarkJob: builder.mutation<any, bookmarkJobRequest>({
      invalidatesTags: ['TalentData', 'TalentDashboard'],
      query: body => ({
        url: `/talent/unbookmark`,
        method: 'PUT',
        body,
      }),
    }),
    parseResume: builder.mutation<any, { locator: string; file: FormData }>({
      query: ({ locator, file }) => ({
        url: `/talent/${locator}/parse-resume`,
        method: 'POST',
        body: file,
      }),
    }),
    sendTalentPasswordResetEmail: builder.mutation<any, { email: string }>({
      query: body => ({
        url: `/password-reset/talent`,
        method: 'POST',
        body,
      }),
    }),
    updateTalentPassword: builder.mutation<
      any,
      { locator: string; password: string }
    >({
      query: ({ locator, password }) => ({
        url: `/talent/password/${locator}`,
        method: 'POST',
        body: { password },
      }),
    }),
    deactivateTalent: builder.mutation<any, string>({
      invalidatesTags: ['TalentData'],
      query: locator => ({
        url: `/talent/deactivate/${locator}`,
        method: 'POST',
      }),
    }),
    activateTalent: builder.mutation<any, string>({
      invalidatesTags: ['TalentData'],
      query: locator => ({
        url: `/talent/activate/${locator}`,
        method: 'POST',
      }),
    }),
    updateTalentResume: builder.mutation<any, UpdateResumeRequest>({
      invalidatesTags: ['TalentData', 'TalentDashboard'],
      query: body => ({
        url: `/talent/resume`,
        method: 'PUT',
        body,
      }),
    }),
    rateTalent: builder.mutation<any, any>({
      query: body => ({
        url: `/talent/${body.talent_locator}/ratings`,
        method: 'PUT',
        body,
      }),
    }),
    SearchTalents: builder.query<any, TalentSearchRequest>({
      query: ({ searchString, skip, take }) => ({
        url: `/talent/search?searchString=${searchString}${
          skip ? `&skip=${skip}` : ''
        }${take ? `&take=${take}` : ''}`,
        method: 'GET',
      }),
    }),
    GetClientsToMessage: builder.query<ComposeMessageClient[], void>({
      query: () => ({
        url: `/talent/compose`,
        method: 'GET',
      }),
    }),
    GetTalentDashboard: builder.query<TalentDashboardResponse, void>({
      providesTags: ['TalentDashboard'],
      query: () => ({
        url: `/talent/dashboard`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useGetTalentsQuery,
  useGetManyTalentsQuery,
  useGetTalentQuery,
  useLazyGetTalentQuery,
  useRegisterTalentsMutation,
  useLoginTalentMutation,
  useUpdateTalentMutation,
  useDeleteTalentMutation,
  useGetTalentResumeQuery,
  useUploadTalentProfilePictureMutation,
  useBookmarkJobMutation,
  useParseResumeMutation,
  useUnbookmarkJobMutation,
  useSendTalentPasswordResetEmailMutation,
  useGetTalentProfilePictureQuery,
  useUpdateTalentPasswordMutation,
  useActivateTalentMutation,
  useDeactivateTalentMutation,
  useUpdateTalentResumeMutation,
  useRateTalentMutation,
  useSearchTalentsQuery,
  useGetClientsToMessageQuery,
  useGetTalentDashboardQuery,
} = talentApi;
